import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { L10N } from 'global/localization'
import { LayoutType } from 'global/types/enums'
import { useGetNotificationDetailQuery } from 'store/notifications'
import { Layout } from 'shared/components/layout'
import { DataHandler } from 'shared/components/data-handler'
import { formatDateTime } from 'shared/services/utils'
import Icon from "shared/components/icon"

export const NotificationDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: notification, isLoading, error } = useGetNotificationDetailQuery(
    Number(id),
    { skip: !id }
  )

  useEffect(() => {
    if (notification) {
      document.title = [notification.title, 'Bluelink Subscription'].join(' - ')
    }
  }, [notification])

  // If notification not found, redirect back to notifications
  useEffect(() => {
    if (error && 'status' in error && error.status === 404) {
      navigate('/notifications')
    }
  }, [error, navigate])

  const handleCtaClick = () => {
    if (notification?.ctaURL) {
      window.open(notification.ctaURL, '_blank')
    }
  }

  return (
    <Layout
      title={L10N.LABEL.NOTIFICATION_DETAIL || 'Notification Detail'}
      type={LayoutType.Header}
    >
      <DataHandler isLoading={isLoading} error={error} onReload={() => window.location.reload()}>
        {notification ? (
          <div className='p-4'>
            <div className='bg-white rounded-lg border border-border overflow-hidden p-4'>
              <div className="flex flex-col justify-center items-center mb-4">
                <h2 className="text-primary mt-3 font-semibold">{notification.title}</h2>
              </div>

              {/* Content */}
              <div className='p-4'>
                <div className='mb-4'>
                  {notification.subtitle && (
                    <h1 className='text-xl font-heading font-medium text-primary mb-2'>
                      {notification.subtitle}
                    </h1>
                  )}
                  <div className='text-sm text-neutral'>
                    {formatDateTime(notification.notificationDate)}
                  </div>
                </div>

                {/* Banner Image */}
                {notification.bannerImage && (
                  <div className='w-full h-48 bg-gray-200 overflow-hidden mb-3'>
                    <img
                      src={notification.bannerImage}
                      alt='Banner'
                      className='w-full h-full object-contain'
                    />
                  </div>
                )}

                <div className='text-base text-neutral leading-relaxed mb-4'>{notification.body}</div>

                {/* CTA Button */}
                {notification.ctaURL && notification.ctaLabel && (
                  <button
                    onClick={handleCtaClick}
                    className='w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-4 rounded-lg transition-colors'
                  >
                    {notification.ctaLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </DataHandler>
    </Layout>
  )
}
